// Country-specific configuration for the "Plan Your Journey" booking flow.
// Single source of truth for both the Albania and Romania booking pages.

export type BookingCountry = 'albania' | 'romania';

export interface ExperienceOption {
  id: string;
  name: string;
  desc: string;
}

export interface BookingConfig {
  country: BookingCountry;
  /** Human-readable country name, e.g. "Albania" */
  label: string;
  /** Hero image for the booking page */
  heroImage: string;
  heroImageAlt: string;
  /** Localised thank-you greeting shown after submit */
  thankYouGreeting: string;
  /** Sub copy under the greeting */
  thankYouSub: string;
  /** Concierge follow-up line */
  thankYouConcierge: string;
  /** Intro copy under "Choose Your Experience" */
  experienceIntro: string;
  experienceOptions: ExperienceOption[];
  accommodationOptions: string[];
  interestOptions: string[];
}

const budgetOptions = ['€1,000 - €3,000', '€3,000 - €5,000', '€5,000 - €10,000', '€10,000 - €20,000', '€20,000+'];

export const bookingBudgetOptions = budgetOptions;

export const bookingSteps = ['Experience', 'Details', 'Preferences', 'Confirm'];

export const bookingConfigs: Record<BookingCountry, BookingConfig> = {
  albania: {
    country: 'albania',
    label: 'Albania',
    heroImage: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775237667349_afcdbb55.jpg',
    heroImageAlt:
      'Plan your bespoke Albania journey: luxury travel concierge, private itineraries across the Albanian Riviera, Alps and UNESCO heritage cities',
    thankYouGreeting: 'Faleminderit',
    thankYouSub: 'Thank you, {name}. Your journey request has been received.',
    thankYouConcierge:
      'Our concierge team will craft your personalized Albanian itinerary and reach out within 24 hours.',
    experienceIntro: 'Select the type of Albanian journey that speaks to you.',
    experienceOptions: [
      { id: 'riviera', name: 'Albanian Riviera', desc: 'Beaches, coves & coastal luxury' },
      { id: 'heritage', name: 'Heritage Cities', desc: 'UNESCO sites & Ottoman culture' },
      { id: 'alps', name: 'Albanian Alps', desc: 'Mountain trekking & alpine retreats' },
      { id: 'yacht', name: 'Yacht Charter', desc: 'Private sailing along the coast' },
      { id: 'villa', name: 'Villa Retreat', desc: 'Exclusive private villa stays' },
      { id: 'culinary', name: 'Culinary Journey', desc: 'Food, wine & local flavors' },
      { id: 'corporate', name: 'Corporate Event', desc: 'Business retreats & team building' },
      { id: 'custom', name: 'Custom Journey', desc: 'Fully bespoke itinerary' },
    ],
    accommodationOptions: ['Luxury Hotel', 'Boutique Hotel', 'Private Villa', 'Mountain Lodge', 'Yacht', 'Mixed'],
    interestOptions: [
      'Beach & Coast',
      'Mountains & Hiking',
      'History & Culture',
      'Food & Wine',
      'Adventure Sports',
      'Wellness & Spa',
      'Photography',
      'Nightlife',
    ],
  },
  romania: {
    country: 'romania',
    label: 'Romania',
    heroImage: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782406694217_33fd396b.png',
    heroImageAlt:
      'Plan your bespoke Romania journey: luxury travel concierge, private itineraries across Transylvania castles, the painted monasteries of Bucovina, the Carpathian mountains and the Danube Delta',
    thankYouGreeting: 'Mulțumim',
    thankYouSub: 'Thank you, {name}. Your journey request has been received.',
    thankYouConcierge:
      'Our concierge team will craft your personalized Romanian itinerary and reach out within 24 hours.',
    experienceIntro: 'Select the type of Romanian journey that speaks to you.',
    experienceOptions: [
      { id: 'transylvania', name: 'Transylvania Castles', desc: 'Bran, Peleș & Gothic fortresses' },
      { id: 'monasteries', name: 'Painted Monasteries', desc: 'UNESCO Bucovina frescoes & heritage' },
      { id: 'carpathians', name: 'Carpathian Wilderness', desc: 'Mountain trekking & brown bear tracking' },
      { id: 'danube', name: 'Danube Delta', desc: 'Wetland safaris & birdwatching cruises' },
      { id: 'wine', name: 'Wine & Culinary', desc: 'Vineyard estates & traditional flavors' },
      { id: 'medieval', name: 'Medieval Cities', desc: 'Sibiu, Brașov & Saxon Transylvania' },
      { id: 'corporate', name: 'Corporate Event', desc: 'Business retreats & team building' },
      { id: 'custom', name: 'Custom Journey', desc: 'Fully bespoke itinerary' },
    ],
    accommodationOptions: ['Luxury Hotel', 'Boutique Hotel', 'Castle Stay', 'Mountain Lodge', 'Vineyard Estate', 'Mixed'],
    interestOptions: [
      'Castles & Fortresses',
      'Mountains & Hiking',
      'History & Culture',
      'Food & Wine',
      'Wildlife & Nature',
      'Wellness & Spa',
      'Photography',
      'Folklore & Villages',
    ],
  },
};
